import { useRef, useState } from 'react';
import {
  IonInput,
  IonButton,
  IonIcon,
  IonText,
  IonRow,
  IonCol,
} from '@ionic/react';
import {
  chevronDownOutline,
  callOutline,
  homeOutline,
  briefcaseOutline,
  receiptOutline,
  receipt,
  trashOutline,
} from 'ionicons/icons';
import { PortalPickerDropdown } from './PortalPickerDropdown';
import TypePickerMenu from './TypePickerMenu';
import styles from './InlineTypePicker.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';

type Mode = 'phone' | 'email' | 'address';

interface TypeConfig {
  value: string;
  label: string;
  icon: string;
}

const TYPE_CONFIGS: Record<Mode, TypeConfig[]> = {
  email: [
    { value: 'personal', get label() { return i18n.t('components.InlineTypePicker.personal'); }, icon: homeOutline },
    { value: 'work', get label() { return i18n.t('components.InlineTypePicker.work'); }, icon: briefcaseOutline },
    { value: 'billing', get label() { return i18n.t('components.InlineTypePicker.billing'); }, icon: receiptOutline },
  ],
  phone: [
    { value: 'mobile', get label() { return i18n.t('components.InlineTypePicker.mobile'); }, icon: callOutline },
    { value: 'work', get label() { return i18n.t('components.InlineTypePicker.work'); }, icon: briefcaseOutline },
    { value: 'home', get label() { return i18n.t('components.InlineTypePicker.home'); }, icon: homeOutline },
    { value: 'fax', get label() { return i18n.t('components.InlineTypePicker.fax'); }, icon: callOutline },
  ],
  address: [
    { value: 'home', get label() { return i18n.t('components.InlineTypePicker.home'); }, icon: homeOutline },
    { value: 'work', get label() { return i18n.t('components.InlineTypePicker.work'); }, icon: briefcaseOutline },
    { value: 'billing', get label() { return i18n.t('components.InlineTypePicker.billing'); }, icon: receiptOutline },
    { value: 'shipping', get label() { return i18n.t('components.InlineTypePicker.shipping'); }, icon: receipt },
  ],
};

interface Props {
  mode: Mode;
  label: string;
  /** Portal container ref — a fixed, inset:0, pointer-events:none IonRow/div. */
  portalRef: React.RefObject<HTMLElement | null>;
  /** Current value of the text input. */
  value: string;
  onValueChange: (value: string) => void;
  /** Current type tag. Matched case-insensitively against the fixed list. */
  currentType: string;
  /** Called with the chosen type's label (e.g. "Mobile") or a custom string. */
  onTypeChange: (type: string) => void;
  inputType?: 'tel' | 'email' | 'text';
  placeholder?: string;
  inputId?: string;
  required?: boolean;
  hasError?: boolean;
  /** "Make Primary" link on the label row — shown when provided. */
  onMakePrimary?: () => void;
  isPrimary?: boolean;
  /** Delete (trash) on the label row — shown when provided. */
  onDelete?: () => void;
}

const InlineTypePicker: React.FC<Props> = ({
  mode,
  label,
  portalRef,
  value,
  onValueChange,
  currentType,
  onTypeChange,
  inputType = 'text',
  placeholder,
  inputId,
  required,
  hasError,
  onMakePrimary,
  isPrimary,
  onDelete,
}) => {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLIonButtonElement>(null);
  const types = TYPE_CONFIGS[mode];

  const normalized = currentType.toLowerCase();
  const known = types.find(t => t.value === normalized);

  const triggerLabel = known
    ? known.label
    : normalized === 'other'
      ? translateDynamic('Other')
      : currentType.charAt(0).toUpperCase() + currentType.slice(1).toLowerCase();

  const close = () => setOpen(false);

  const selectType = (type: string) => {
    onTypeChange(type);
    close();
  };

  return (
    <>
      {/* No IonItem — IonItem forwards taps anywhere in its box to the input.
          Plain IonRow/IonCol container instead: a label row (label + actions)
          above the bordered input box, only the box itself focuses. */}
      <IonRow className={styles.fieldRow}>
        <IonCol size="12" className={styles.fieldLabel}>
          <IonText className={styles.fieldLabelText}>{label}</IonText>
          {(onMakePrimary || onDelete) && (
            <IonText className={styles.fieldActions}>
              {onMakePrimary && (
                <IonText
                  role="button"
                  tabIndex={0}
                  className={`${styles.actionLink} ${isPrimary ? styles.actionLinkPrimary : ''}`}
                  onClick={onMakePrimary}
                >{tr('components.InlineTypePicker.makePrimary')}
                </IonText>
              )}
              {onDelete && (
                <IonIcon
                  role="button"
                  tabIndex={0}
                  icon={trashOutline}
                  className={styles.actionDelete}
                  onClick={onDelete}
                />
              )}
            </IonText>
          )}
        </IonCol>
        <IonCol size="12">
          <IonInput
            className={`${styles.inputBox} ${hasError ? styles.inputBoxError : ''}`}
            id={inputId}
            type={inputType}
            value={value}
            placeholder={placeholder}
            required={required}
            onIonInput={e => onValueChange(e.detail.value ?? '')}
          >
            <IonButton
              slot="end"
              ref={triggerRef}
              fill="clear"
              className={`${styles.typeTrigger} ${open ? styles.typeTriggerOpen : ''}`}
              onClick={() => setOpen(o => !o)}
            >
              {triggerLabel}
              <IonIcon icon={chevronDownOutline} className={styles.typeCaret} />
            </IonButton>
          </IonInput>
        </IonCol>
      </IonRow>

      <PortalPickerDropdown
        isOpen={open}
        fieldRef={triggerRef as React.RefObject<HTMLElement | null>}
        portalRef={portalRef}
        close={close}
        align="right"
        className={styles.dropdown}
      >
        <TypePickerMenu
          types={types}
          currentType={currentType}
          onSelect={selectType}
          onClose={close}
        />
      </PortalPickerDropdown>
    </>
  );
};

export default InlineTypePicker;
